import { useRouter } from '@tanstack/react-router';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'cn';
import { Slot } from 'radix-ui';

/*
 * O Link: texto que leva a algum lugar. `default` é a cor da marca; `subtle` tem a cor do texto ao redor e só se revela como link no hover
 * (o nome do arquivo na lista). Nos dois, o sublinhado aparece no hover — é o sinal
 * de "clicável" que o cursor sozinho não dá a quem navega pelo teclado.
 *
 * `asChild` deixa o Link vestir o `<Link>` do router: a aparência é daqui, a navegação é
 * de lá. Um `href` interno ("/app/recurso/id", o que `useUrlOf` compõe) também navega
 * pelo router: o clique simples troca a rota sem recarregar a página — recarregar zerava
 * tudo que vive em memória. Ctrl/Cmd/Shift, botão do meio e `target` continuam do navegador.
 */
// `truncate` pede caixa de bloco: num contêiner flex o texto é cortado sem as reticências
// (e alinhado à esquerda, que um `<button>` em bloco centraliza).
const linkVariants = cva(
  'inline-flex cursor-pointer [&.truncate]:block [&.truncate]:text-left items-center gap-1 rounded-sm underline-offset-2 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'text-brand-fg hover:text-brand-hover active:text-brand-pressed',
        subtle: 'text-foreground hover:text-foreground active:text-fg-secondary',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

function isInternal(href: unknown): href is string {
  return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
}

function Link({
  className,
  variant,
  asChild = false,
  href,
  target,
  download,
  onClick,
  ...props
}: React.ComponentProps<'a'> & VariantProps<typeof linkVariants> & { asChild?: boolean }) {
  // Fora do RouterProvider (teste, história) o link volta a ser uma âncora comum.
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined;
  const Comp = asChild ? Slot.Root : 'a';
  // `download` é do navegador: um arquivo servido na mesma origem não é rota.
  const routed = !asChild && router !== undefined && download === undefined && isInternal(href);
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (!routed || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    if (target && target !== '_self') return;
    event.preventDefault();
    router.history.push(href);
  };
  return (
    <Comp
      data-slot="link"
      className={cn(linkVariants({ variant, className }))}
      href={href}
      target={target}
      download={download}
      onClick={handleClick}
      {...props}
    />
  );
}

export { Link, linkVariants };
